/* BB product buy box: variant selection, bundle tiers, live totals, add to cart and the media gallery. */
const BB = window.BB;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class BBProduct extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-bb-product-json]');
    if (!json) return;
    this.data = JSON.parse(json.textContent);
    this.variants = this.data.variants;
    this.form = this.querySelector('[data-bb-product-form]');
    this.idInput = this.querySelector('[data-bb-variant-id]');
    this.qtyInput = this.querySelector('[data-bb-qty-input]');
    this.atc = this.querySelector('[data-bb-atc]');
    this.error = this.querySelector('[data-bb-product-error]');
    this.fieldsets = Array.from(this.querySelectorAll('.bb-option'));
    this.tierInputs = Array.from(this.querySelectorAll('[data-bb-tier]'));
    this.track = this.querySelector('[data-bb-gallery-track]');
    this.slides = this.track ? Array.from(this.track.children) : [];
    this.thumbStrip = this.querySelector('[data-bb-gallery-thumbs]');
    this.thumbs = Array.from(this.querySelectorAll('[data-bb-thumb]'));
    this.index = 0;
    this.busy = false;
    this.variant = this.variants.find((variant) => variant.id === Number(this.idInput.value)) || this.variants[0];

    this.addEventListener('change', (event) => this.onChange(event));
    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('submit', (event) => this.onSubmit(event));
    if (this.track) {
      let frame = 0;
      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => this.setActive(Math.round(this.track.scrollLeft / this.track.clientWidth)));
        },
        { passive: true }
      );
    }
    this.render();
  }

  text(selector, value, root = this) {
    const node = root.querySelector(selector);
    if (!node) return null;
    if (value !== undefined) node.textContent = value;
    return node;
  }

  get quantity() {
    return Math.min(99, Math.max(1, Math.floor(Number(this.qtyInput.value)) || 1));
  }

  get tier() {
    const checked = this.tierInputs.find((input) => input.checked);
    return checked ? this.variant.tiers[Number(checked.value)] : null;
  }

  onChange(event) {
    const target = event.target;
    if (target.matches('[data-bb-option]')) {
      this.selectOption(Number(target.dataset.bbOption));
    } else if (target.matches('[data-bb-tier]')) {
      this.render();
    } else if (target.matches('[data-bb-qty-input]')) {
      this.qtyInput.value = String(this.quantity);
      this.render();
    }
  }

  onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const step = target.closest('[data-bb-qty]');
    if (step) {
      this.qtyInput.value = String(Math.min(99, Math.max(1, this.quantity + Number(step.dataset.bbQty))));
      this.render();
      return;
    }
    const thumb = target.closest('[data-bb-thumb]');
    if (thumb) {
      this.showMedia(Number(thumb.dataset.bbThumb));
      return;
    }
    if (target.closest('[data-bb-gallery-prev]')) this.showMedia((this.index - 1 + this.slides.length) % this.slides.length);
    if (target.closest('[data-bb-gallery-next]')) this.showMedia((this.index + 1) % this.slides.length);
  }

  selectOption(changed) {
    const selected = this.fieldsets.map((fieldset) => {
      const input = fieldset.querySelector('input:checked');
      return input ? input.value : null;
    });
    let variant = this.variants.find((candidate) => candidate.options.every((value, position) => value === selected[position]));
    if (!variant) {
      const candidates = this.variants.filter((candidate) => candidate.options[changed] === selected[changed]);
      variant = candidates.find((candidate) => candidate.available) || candidates[0];
    }
    if (variant) this.setVariant(variant);
  }

  setVariant(variant) {
    this.variant = variant;
    this.idInput.value = String(variant.id);
    this.fieldsets.forEach((fieldset, position) => {
      fieldset.querySelectorAll('input').forEach((input) => {
        input.checked = input.value === variant.options[position];
      });
      this.text('[data-bb-option-value]', variant.options[position], fieldset);
    });
    if (this.hasAttribute('data-update-url')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url);
    }
    if (variant.mediaId) {
      this.showMedia(this.slides.findIndex((slide) => Number(slide.dataset.mediaId) === variant.mediaId));
    }
    this.render();
    this.dispatchEvent(new CustomEvent('bb:variant:change', { bubbles: true, detail: { variant } }));
  }

  updateOptionStates() {
    const current = this.variant.options;
    this.fieldsets.forEach((fieldset, position) => {
      fieldset.querySelectorAll('input').forEach((input) => {
        const withValue = this.variants.filter((candidate) => candidate.options[position] === input.value);
        const exists = withValue.some((candidate) => candidate.options.every((value, index) => index >= position || value === current[index]));
        const exact = withValue.filter((candidate) => candidate.options.every((value, index) => index === position || value === current[index]));
        const soldOut = exists && exact.length > 0 && !exact.some((candidate) => candidate.available);
        input.disabled = !exists;
        input.classList.toggle('is-soldout', soldOut);
        const state = input.nextElementSibling.querySelector('[data-bb-option-state]');
        if (state) state.textContent = !exists ? `, ${this.data.unavailableLabel}` : soldOut ? `, ${this.data.soldOutLabel}` : '';
      });
    });
  }

  render() {
    const variant = this.variant;
    this.updateOptionStates();

    this.tierInputs.forEach((input, index) => {
      const tier = variant.tiers[index];
      const row = input.closest('.bb-tier');
      input.disabled = !tier.available;
      this.text('[data-bb-tier-total]', tier.totalMoney, row);
      const was = this.text('[data-bb-tier-was]', tier.compareMoney, row);
      if (was) was.hidden = !tier.compareMoney;
      const each = this.text('[data-bb-tier-each]', tier.each, row);
      if (each) each.hidden = tier.qty < 2;
    });
    const checked = this.tierInputs.find((input) => input.checked);
    if (this.tierInputs.length && (!checked || checked.disabled)) {
      if (checked) checked.checked = false;
      const firstAvailable = this.tierInputs.find((input) => !input.disabled);
      if (firstAvailable) firstAvailable.checked = true;
    }

    const tier = this.tier;
    const quantity = this.quantity;
    const available = tier ? tier.available : this.tierInputs.length ? false : variant.available;
    const compare = tier ? tier.compareMoney : variant.compareMoney;
    const meta = tier && tier.qty > 1 ? `${tier.units} · ${tier.each}` : variant.unitMoney;

    this.text('[data-bb-price-now]', tier ? tier.totalMoney : variant.priceMoney);
    const was = this.text('[data-bb-price-was]', compare);
    if (was) was.hidden = !compare || !this.data.showCompare;
    const metaNode = this.text('[data-bb-price-meta]', meta);
    if (metaNode) metaNode.hidden = !meta;
    this.text('[data-bb-variant-title]', variant.title);
    const sku = this.querySelector('[data-bb-sku]');
    if (sku) {
      sku.hidden = !variant.sku;
      this.text('[data-bb-sku-value]', variant.sku || '');
    }
    const stock = this.text('[data-bb-stock]', variant.stock);
    if (stock) stock.hidden = !variant.stock;

    if (this.atc && !this.busy) {
      const cents = (tier ? tier.total : variant.price) * quantity;
      this.atc.disabled = !available;
      this.text('[data-bb-atc-label]', available ? this.data.addLabel : this.data.soldOutLabel, this.atc);
      const total = this.atc.querySelector('[data-bb-atc-total]');
      if (total) {
        total.hidden = !available;
        this.text('[data-bb-atc-amount]', quantity === 1 ? (tier ? tier.totalMoney : variant.priceMoney) : BB.formatMoney(cents), total);
      }
    }

    const unitCents = tier ? tier.total : variant.price;
    this.dispatchEvent(
      new CustomEvent('bb:product:render', {
        bubbles: true,
        detail: {
          id: tier ? tier.id : variant.id,
          available,
          summary: tier && tier.qty > 1 ? `${variant.title} · ${tier.units}` : variant.title,
          totalText: quantity === 1 ? (tier ? tier.totalMoney : variant.priceMoney) : BB.formatMoney(unitCents * quantity),
        },
      })
    );
  }

  async onSubmit(event) {
    if (event.target !== this.form) return;
    event.preventDefault();
    if (this.busy) return;
    const tier = this.tier;
    const item = tier ? { id: tier.id, quantity: tier.qty * this.quantity } : { id: this.variant.id, quantity: this.quantity };
    this.busy = true;
    this.error.hidden = true;
    this.atc.disabled = true;
    this.atc.setAttribute('aria-busy', 'true');
    try {
      const opener = this.submitOpener || this.atc;
      this.submitOpener = null;
      await BB.cart.add([item], opener);
    } catch (error) {
      this.error.textContent = error.message;
      this.error.hidden = false;
    } finally {
      this.busy = false;
      this.atc.removeAttribute('aria-busy');
      this.render();
    }
  }

  showMedia(index) {
    if (!this.track || index < 0 || index >= this.slides.length) return;
    this.track.scrollTo({ left: index * this.track.clientWidth, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    this.setActive(index);
  }

  setActive(index) {
    if (index === this.index || index < 0 || index >= this.slides.length) return;
    const previous = this.slides[this.index].querySelector('video');
    if (previous) previous.pause();
    this.index = index;
    this.thumbs.forEach((thumb, position) => {
      if (position === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    const active = this.thumbs[index];
    if (active && this.thumbStrip) {
      const left = active.offsetLeft - this.thumbStrip.offsetLeft;
      if (left < this.thumbStrip.scrollLeft || left + active.offsetWidth > this.thumbStrip.scrollLeft + this.thumbStrip.clientWidth) {
        this.thumbStrip.scrollTo({ left: left - this.thumbStrip.clientWidth / 2 + active.offsetWidth / 2, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
    }
  }
}

if (!customElements.get('bb-product')) customElements.define('bb-product', BBProduct);
